import { create } from 'zustand';
import type { Permission, Store } from '@zeroth/shared';

export interface AccountRoleSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isSystem: boolean;
  isOwnerRole: boolean;
  permissions: Permission[];
}

export interface AccountMembershipSummary {
  id: string;
  status: string;
  role: AccountRoleSummary;
}

export interface AccountExperimentalFeatures {
  walkieTalkie: boolean;
  shipmentSourcing?: boolean;
}

export interface AccountRecognition {
  kind: 'FOUNDING_ACCOUNT';
  benefit: {
    product: 'RESEARCH_SCANNER';
    billing: 'INCLUDED';
    standardMonthlyPriceUsd: number;
    monthlyEnrichmentLimit: number;
  };
}

export interface AccountMe {
  account: {
    id: string;
    name: string;
  };
  recognition?: AccountRecognition | null;
  experimentalFeatures: AccountExperimentalFeatures;
  membership: AccountMembershipSummary;
  permissions: Permission[];
  stores: Store[];
}

interface AccountState {
  currentAccountId: string | null;
  account: AccountMe['account'] | null;
  recognition: AccountRecognition | null;
  experimentalFeatures: AccountExperimentalFeatures;
  membership: AccountMembershipSummary | null;
  permissions: Permission[];
  accessibleStores: Store[];
  setCurrentAccountId: (accountId: string | null) => void;
  setAccount: (account: AccountMe) => void;
  clearAccount: () => void;
}

const initialState = {
  currentAccountId: null,
  account: null,
  recognition: null,
  experimentalFeatures: { walkieTalkie: false, shipmentSourcing: false },
  membership: null,
  permissions: [] as Permission[],
  accessibleStores: [] as Store[],
};

// Account selection is server-authoritative. Remove the former persisted id so
// an accepted invitation cannot be bootstrapped with a previous account header.
try {
  globalThis.localStorage?.removeItem('zeroth-account');
} catch {
  // Storage may be disabled; account state remains safely in memory.
}

export const useAccountStore = create<AccountState>()((set) => ({
  ...initialState,
  setCurrentAccountId: (accountId) => set({ currentAccountId: accountId }),
  setAccount: (account) =>
    set({
      currentAccountId: account.account.id,
      account: account.account,
      recognition: account.recognition ?? null,
      experimentalFeatures: { ...account.experimentalFeatures, shipmentSourcing: account.experimentalFeatures.shipmentSourcing === true },
      membership: account.membership,
      permissions: account.permissions,
      accessibleStores: account.stores,
    }),
  clearAccount: () => set(initialState),
}));

export const useCurrentAccountId = () => useAccountStore((state) => state.currentAccountId);
export const useAccountRecognition = () => useAccountStore((state) => state.recognition);
export const useAccountPermissions = () => useAccountStore((state) => state.permissions);
export const useAccountExperimentalFeatures = () => useAccountStore((state) => state.experimentalFeatures);

export const useShipmentSourcingEnabled = () => useAccountStore((state) => state.experimentalFeatures.shipmentSourcing === true);
